import type { Cookies } from '@mx/util/get-all-cookie';
import { getAllCookie } from '@mx/util/get-all-cookie';
import type { QueryStrings } from '@mx/util/get-all-query-string';
import { getAllQueryString } from '@mx/util/get-all-query-string';
import { getApp } from '@mx/util/get-app';
import { getSystem } from '@mx/util/get-system';
import { getUA } from '@mx/util/get-ua';
import { getCommonParams } from '@mx/pay-common';

class Env {
  uid: string;

  lat: string;

  lng: string;

  app: string;

  stId: string;

  uuid: string = '';

  token: string;

  // tslint:disable-next-line:variable-name
  nb_uuid: string;

  entry: string;

  system: string;

  cityId: string;

  version: string;

  platform: string;

  isNative: boolean = /knb/.test(navigator.userAgent.toLowerCase());

  isHarmony: boolean = /openharmony/.test(navigator.userAgent.toLowerCase());

  inWeixinMini: boolean = false;

  isWechat: boolean = /micromessenger/.test(navigator.userAgent.toLowerCase());

  isWXMiniProgram: boolean = /miniprogram/.test(navigator.userAgent.toLowerCase());

  // 美团极简版
  inMeituanTurbo: boolean = navigator.userAgent.toLowerCase().indexOf('meituanturbo') > -1;

  inMMP: boolean = navigator.userAgent.toLowerCase().indexOf('miniprogram') > -1;

  isInMeituan: boolean = /meituangroup/.test(navigator.userAgent.toLowerCase());

  utmTerm: string;

  utmMedium: string;

  utmCampaign: string;

  utmSource: string;

  utmContent: string;

  cookies: Cookies;

  queryStrings: QueryStrings;

  fingerprint = '';

  wechatAppId = '';

  layerType: string;

  commonParams: {
    token?: string;
    nb_app: string;
    nb_uuid: string;
    nb_appversion: string;
    userid: string;
  };

  // 接入方
  scene: string;

  // 绑卡接入参数
  merchantNo: string;

  // 充值来源
  rechargeSource: string;

  // 鲸湾活动id
  campaignId: string;

  // 余额参数
  nbSource: 'balance_withdraw' | 'balance_recharge' = 'balance_withdraw';

  readonly extDimStatEntryWithdraw: string = 'wallet_ibalance_withdraw';

  readonly extDimStatEntryRecharge: string = 'wallet_ibalance_recharge';

  indexData: MainPageData = {} as MainPageData;

  osVersion: string;

  pgyData: any;

  pgyDataReady: boolean;

  constructor() {
    const { getParams } = this;

    this.cookies = getAllCookie();
    this.queryStrings = getAllQueryString();
    // utm 参数
    this.utmTerm = getParams('utm_term'); // app版本
    this.utmMedium = getParams('utm_medium'); // 设备系统
    this.utmSource = getParams('utm_source') || '-999';
    this.utmContent = getParams('utm_content');
    this.utmCampaign = getParams('utm_campaign');

    // 环境参数
    this.lat = getParams('lat');
    this.lng = getParams('lng');
    this.stId = getParams('stid');
    this.cityId = getParams('ci') || '0';
    this.entry = getParams('entry');
    this.uid = getParams('userid');
    this.uuid = getParams('iuuid') || getParams('uuid');
    this.token = this.queryStrings.token;
    this.layerType = this.queryStrings.layerType;

    // nb_uuid 逻辑对齐H5绑卡和i版收银台
    const { uuid, iuuid, mtuuid } = this.cookies;
    const payUUID = (uuid || '').split('.')[0];
    this.nb_uuid = iuuid || payUUID || mtuuid || 'empty';

    this.version = getParams('version_name') || this.utmTerm;

    // 解析参数
    this.app = getApp(this.utmCampaign);
    this.system = getSystem();
    if (this.system === 'ios') {
      this.system = 'iphone';
    }
    this.platform = this.isWXMiniProgram ? 'wx_mini_program' : getCommonParams().nb_platform;

    this.commonParams = {
      nb_app: this.app,
      nb_uuid: this.nb_uuid,
      nb_appversion: this.version,
      userid: this.uid,
    };

    this.scene = this.queryStrings.scene;
    // 饭卡发奖所需参数
    this.campaignId = this.queryStrings.campaignId;
    // 充值来源
    this.rechargeSource = this.queryStrings.rechargeSource;

    // 绑卡merchant_no
    this.merchantNo = this.scene === 'wallet_sdk' ? '10019' : '10003';

    this.setFingerprint();

    this.osVersion = getUA().osVersion;
    // 页面同步率实验策略
    this.pgyData = {};
    this.pgyDataReady = false;

    if (this.isWXMiniProgram) {
      this.wechatAppId = this.getMiniProgramIdFromUA();
    }
  }

  // 获取小程序id
  getMiniProgramIdFromUA() {
    const ua = navigator.userAgent;
    // 如果输入为空，直接返回''
    if (!ua) {
      return '';
    }
    // 使用正则表达式匹配 "miniProgram/" 后面的内容
    const regex = /miniProgram\/([a-zA-Z0-9]+)/;
    const match = ua.match(regex);
    // 如果匹配成功，返回第一个捕获组（即小程序ID）
    if (match && match[1]) {
      return match[1];
    }
    // 如果没有匹配到，返回''
    return '';
  }

  setFingerprint() {
    // 获取风控指纹信息
    if (this.isNative && !this.isHarmony) {
      KNB.ready(() => {
        KNB.getFingerprint({
          success: (data: any) => {
            this.fingerprint = data.fingerprint;
          },
        });
      });
    }
  }

  setIndexData(val: MainPageData) {
    this.indexData = val;
  }

  updateEnvParam<T extends keyof EnvCommonParams>(key: T, value: EnvCommonParams[T]): void {
    this[key] = value;
  }

  private readonly getParams = (key: string): string => {
    return this.queryStrings[key] || this.cookies[key] || '';
  };

  /**
   * @fn dsp接口返回PGY-ssr-Data集合
   * @param {Object}
   */
  setPgyData(val: any) {
    this.pgyData = val;
    this.pgyDataReady = true;
  }
}

export const env = new Env();
